import React from 'react'
import classnames from 'classnames'
import { lossWhenPositive, moneyColorClass } from '../cellColor'
import NullDash from './NullDash'

export type MoneyCellRole = 'money' | 'loss'

export interface MoneyCellProps {
  value: number | null | undefined
  /** 0 for whole dollars; 2 for cents. Default 2. */
  fractionDigits?: 0 | 2
  className?: string
  /** Semantic coloring; overridden by explicit valueClassName. */
  role?: MoneyCellRole
  /** Optional class for the numeric portion (the $ stays muted). */
  valueClassName?: string
}

const roleColorClass = (role: MoneyCellRole | undefined, value: number): string | undefined => {
  if (role === 'money') return moneyColorClass(value)
  if (role === 'loss') return lossWhenPositive(value)
  return undefined
}

/** Table-friendly currency: nowrap, tabular number, muted $ prefix with a space. */
export const MoneyCell: React.FC<MoneyCellProps> = ({
  value,
  fractionDigits = 2,
  className,
  role,
  valueClassName
}) => {
  if (value == null || Number.isNaN(value)) {
    return <NullDash className={className} />
  }
  const formatted =
    fractionDigits === 0
      ? Math.round(value).toLocaleString()
      : value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const colorClass = classnames(roleColorClass(role, value), valueClassName)
  return (
    <span className={classnames('whitespace-nowrap tabular-nums', className)}>
      <span className="text-xs font-normal text-slate-400 dark:text-slate-400">$</span>
      <span className={classnames('ml-1', colorClass)}>{formatted}</span>
    </span>
  )
}

export default MoneyCell
